import React, { useState, useEffect } from 'react';
import { Star } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

/**
 * Modal for writing OR editing a review on an entity.
 *
 * Props
 *  - open / onOpenChange  — controlled dialog state
 *  - entityName            — shown in the dialog title
 *  - existing              — when set, pre-fills the form for editing
 *  - onSubmit({rating, title, body})  → Promise<boolean>; closes on true
 */
const WriteReviewDialog = ({ open, onOpenChange, entityName, existing, onSubmit }) => {
  const [rating, setRating] = useState(existing?.rating || 5);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState(existing?.title || '');
  const [body, setBody] = useState(existing?.body || '');
  const [submitting, setSubmitting] = useState(false);

  // Reset form when the dialog (re-)opens or the source review changes.
  useEffect(() => {
    if (open) {
      setRating(existing?.rating || 5);
      setHoverRating(0);
      setTitle(existing?.title || '');
      setBody(existing?.body || '');
    }
  }, [open, existing]);

  const handleSubmit = async () => {
    if (body.trim().length < 3) return;
    setSubmitting(true);
    const ok = await onSubmit({ rating, title: title.trim() || null, body: body.trim() });
    setSubmitting(false);
    if (ok) onOpenChange(false);
  };

  const displayRating = hoverRating || rating;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="write-review-dialog">
        <DialogHeader>
          <DialogTitle>{existing ? 'Edit your review' : 'Write a review'}</DialogTitle>
          <DialogDescription>
            Share your experience at <span className="font-medium text-foreground">{entityName}</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Star picker */}
          <div className="space-y-1.5">
            <Label className="text-sm">Your rating</Label>
            <div className="flex items-center gap-1" data-testid="review-rating-picker">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  type="button"
                  key={n}
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHoverRating(n)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-1 transition-transform hover:scale-110"
                  data-testid={`review-star-${n}`}
                  aria-label={`Rate ${n} star${n > 1 ? 's' : ''}`}
                >
                  <Star
                    className={`h-7 w-7 ${
                      n <= displayRating
                        ? 'fill-amber-400 text-amber-400'
                        : 'text-muted-foreground/40'
                    }`}
                  />
                </button>
              ))}
              <span className="ml-2 text-sm text-muted-foreground" data-testid="review-rating-value">
                {displayRating}/5
              </span>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="review-title">Title (optional)</Label>
            <Input
              id="review-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              placeholder="Sum it up in a line"
              data-testid="review-title-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="review-body">Your review</Label>
            <Textarea
              id="review-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={2000}
              rows={5}
              placeholder="What stood out? What would future visitors want to know?"
              data-testid="review-body-input"
            />
            <p className="text-xs text-muted-foreground text-right">{body.length}/2000</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="review-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || body.trim().length < 3}
            data-testid="review-submit-btn"
          >
            {submitting ? 'Posting…' : existing ? 'Save changes' : 'Post review'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WriteReviewDialog;
